import React from "react";
import ConditionRow from "../components/ConditionRow";
import { ColumnSourceEnum, PropertyBrowserNodeOUT } from "api-client";
import { PROCESSING_RULE_OPERATORS } from "features/DynamicTable/consts";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import { IconPlus } from "@tabler/icons-react";
import clsx from "clsx";
import { rulesSetStore } from "../RulesSetStore";
import { observer } from "mobx-react-lite";

export const EMPTY_CONDITION = {
  value: "",
  valueProperty: undefined,
  operator: PROCESSING_RULE_OPERATORS[0].id,
  property: undefined,
};

const RuleConditions = () => {
  const { t } = useTranslation();
  const conditions = rulesSetStore.currentRule?.conditions || [];

  const handleCopyRule = async (index: number) => {
    const newConditions = [...conditions, { ...conditions[index] }];
    rulesSetStore.setRuleConditions(newConditions);
    await rulesSetStore.updateRule();
  };

  const handleRemoveCondition = (index: number) => {
    rulesSetStore.setRuleConditions(conditions.filter((_, i) => i !== index));
    rulesSetStore.updateRule();
  };

  const handleSelectProperty = async (
    index: number,
    property: PropertyBrowserNodeOUT | undefined
  ) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    currentCondition.property = property;
    rulesSetStore.setRuleConditions(newConditions);
    await rulesSetStore.updateRule();
  };

  const handleSelectOperator = async (
    index: number,
    operator: string | undefined | number,
    updateState?: boolean
  ) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    currentCondition.operator = String(operator);
    if (currentCondition.operator.includes("EMPTY")) {
      currentCondition.value = "";
      currentCondition.valueProperty = undefined;
    }
    rulesSetStore.setRuleConditions(newConditions);
    if (updateState) {
      await rulesSetStore.updateRule();
    }
  };

  const handleSelectValue = async (
    index: number,
    value: PropertyBrowserNodeOUT | string | undefined
  ) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    if (typeof value === "string") {
      currentCondition.value = value;
      currentCondition.value_source = ColumnSourceEnum.Literal;
    } else {
      currentCondition.valueProperty = value;
      currentCondition.value = "";
      currentCondition.value_source = value?.column_source as ColumnSourceEnum;
    }
    rulesSetStore.setRuleConditions(newConditions);
    await rulesSetStore.updateRule();
  };

  const handleRemoveValue = async (index: number) => {
    const newConditions = [...conditions];
    const currentCondition = newConditions[index];
    currentCondition.value = "";
    currentCondition.valueProperty = undefined;
    currentCondition.value_source = undefined;
    rulesSetStore.setRuleConditions(newConditions);
    await rulesSetStore.updateRule();
  };

  const addNewCondition = async () => {
    rulesSetStore.setRuleConditions([...conditions, { ...EMPTY_CONDITION }]);
    rulesSetStore.setInProgressRuleID(rulesSetStore.currentRule?.id);
    await rulesSetStore.updateRule();
  };

  return (
    <div className="p-6 flex flex-col justify-items-center items-center flex-grow">
      {conditions.map((item, index) => (
        <ConditionRow
          key={index}
          index={index}
          row={item}
          onCopyRow={() => handleCopyRule(index)}
          onRemoveRow={
            conditions.length > 1
              ? () => handleRemoveCondition(index)
              : undefined
          }
          onSelectProperty={(property) => handleSelectProperty(index, property)}
          onSelectOperator={(operator, updateState) =>
            handleSelectOperator(index, operator, updateState)
          }
          onSelectValue={(value) => handleSelectValue(index, value)}
          onRemoveProperty={() => handleSelectProperty(index, undefined)}
          onRemoveValue={() => handleRemoveValue(index)}
        />
      ))}
      <Button
        type="link"
        className={clsx(
          "text-indigo-700 text-base font-medium mt-1 hover:!bg-indigo-50 hover:!text-indigo-700 !w-fit"
        )}
        width="fit-content"
        leadingIcon={<IconPlus size={20} stroke={2.5} />}
        onClick={addNewCondition}
      >
        {t("rulesEditor.addFurtherCondition")}
      </Button>
    </div>
  );
};

export default observer(RuleConditions);
